import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Export billing data to Excel file
 * @param {string} provider - Provider name (e.g., "Airtel", "BSNL")
 * @param {Array} data - Billing data array
 * @param {Object} totals - Totals object with sims and bill
 * @param {string} month - Month string (e.g., "December 2025")
 */
export const exportBillingToExcel = (provider, data, totals, month = "Current") => {
  const fileName = `${provider}_Bill_${month.replace(/\s/g, "_")}.xlsx`;

  // Prepare data for Excel
  const excelData = data.map((item, index) => ({
    "Sr. No": index + 1,
    "Plan Name": item.planName,
    "Total SIMs": item.totalSims,
    "Monthly Bill (₹)": item.monthlyBill,
  }));

  // Add totals row
  excelData.push({
    "Sr. No": "",
    "Plan Name": "TOTAL",
    "Total SIMs": totals.sims,
    "Monthly Bill (₹)": totals.bill,
  });

  // Create worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);

  // Set column widths
  ws["!cols"] = [
    { wch: 8 },  // Sr. No
    { wch: 25 }, // Plan Name
    { wch: 12 }, // Total SIMs
    { wch: 18 }, // Monthly Bill
  ];

  // Create workbook and save
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `${provider} Bill`);
  XLSX.writeFile(wb, fileName);
};

/**
 * Export billing data to PDF file
 * @param {string} provider - Provider name (e.g., "Airtel", "BSNL")
 * @param {Array} data - Billing data array
 * @param {Object} totals - Totals object with sims and bill
 * @param {string} month - Month string (e.g., "December 2025")
 */
export const exportBillingToPDF = (provider, data, totals, month = "Current") => {
  const fileName = `${provider}_Bill_${month.replace(/\s/g, "_")}.pdf`;

  const doc = new jsPDF();

  // Add title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(`${provider} - Monthly Bill`, 14, 20);

  // Add month
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(`Period: ${month}`, 14, 30);

  // Prepare table data
  const tableHeaders = [["Sr. No", "Plan Name", "Total SIMs", "Monthly Bill (Rs.)"]];
  const tableData = data.map((item, index) => [
    index + 1,
    item.planName,
    item.totalSims.toLocaleString(),
    `Rs. ${item.monthlyBill.toLocaleString()}`,
  ]);

  // Add totals row
  tableData.push([
    "",
    "TOTAL",
    totals.sims.toLocaleString(),
    `Rs. ${totals.bill.toLocaleString()}`,
  ]);

  // Generate table
  doc.autoTable({
    head: tableHeaders,
    body: tableData,
    startY: 40,
    theme: "grid",
    headStyles: {
      fillColor: provider === "Airtel" ? [228, 29, 44] : [0, 102, 179],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    footStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    styles: {
      fontSize: 10,
      cellPadding: 5,
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 25 },
      1: { halign: "left", cellWidth: 60 },
      2: { halign: "center", cellWidth: 40 },
      3: { halign: "right", cellWidth: 55 },
    },
    // Style the last row (totals) differently
    didParseCell: function (data) {
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = [240, 240, 240];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  // Add footer with generation date
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, pageHeight - 10);

  // Save PDF
  doc.save(fileName);
};
